'use strict';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/of';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {isAndroidBuild, isIosBuild} from '../../config/constants';
import {getIsOnline} from '../../settings/reducers';
import {$settingsProvider} from '../../settings/services/AppSettingsService';
import {NoteState} from '../../system/enums/NoteState';
import {GlobalEvents} from '../enums/GlobalEvents';
import {readAsDataURL} from '../rx-utils';
import {FileDataCacheService} from './FileDataCacheService';
import {UploadStatus} from './UPLOAD_STATUS';


const angular = require('angular');
const {extend} = angular;

PersistentUploaderService.$inject = [
  '$log',
  '$injector',
  '$parse',
  '$rootScope',
  'EventBus',
  '$ngRedux',
  '$persistentCacheFactory',
  FileDataCacheService.SERVICE_NAME,
  $settingsProvider.SERVICE_NAME,
  '$filter',
  '$exceptionHandler',
  'FileDomain',
  'FirebaseConnectionService',
  'NotificationService',
  'DeviceServices',
  'FileUrlCache',
];

PersistentUploaderService.SERVICE_NAME = 'PersistentUploaderService';

/**
 * @param $log
 * @param $injector
 * @param $parse
 * @param $rootScope
 * @param EventBus
 * @param $ngRedux
 * @param $persistentCacheFactory
 * @param {FileDataCacheService} fileDataCacheService
 * @param {AppSettings} $settings
 * @param $filter
 * @param $exceptionHandler
 * @param FileDomain
 * @param FirebaseConnectionService
 * @param NotificationService
 * @param DeviceServices
 * @param FileUrlCache
 * @constructor
 */
export function PersistentUploaderService(
  $log,
  $injector,
  $parse,
  $rootScope,
  EventBus,
  $ngRedux,
  $persistentCacheFactory,
  fileDataCacheService,
  $settings,
  $filter,
  $exceptionHandler,
  FileDomain,
  FirebaseConnectionService,
  NotificationService,
  DeviceServices,
  FileUrlCache,
) {
  window.addEventListener('beforeunload', onbeforeunload);
  var fileCache = {};
  var fileCacheSize = 0;
  var modelIdTotalCache = {};
  var sizeSubject = new Subject();
  var files = {};
  var uploadsInProgress = {}; // store the promise corresponding to the upload operation for the corresponding key
  var uploadsCount = 0;
  var uploadQueue = [];
  var uploadQueueIndex = {};
  var MAX_CONCURRENT_UPLOADS = DeviceServices.isWebView() ? 5 : 15; // TODO: increase limit as we can

  // API

  const self = this;
  this.init = init;
  this.getFile = getFile;
  this.getFileData = getFileData;
  this.getByKey = getByKey;
  this.getByKey$ = getByKey$;
  this.getTotalByKey = getTotalByKey;
  this.getAll = getAll;
  this.getAll$ = getAll$;
  this.addFile = addFile;
  this.addFiles = addFiles;
  this.getFileUrlData = getFileUrlData;
  this.addFileUrlData = addFileUrlData;
  this.getReadAsDataURL$ = getReadAsDataURL$;
  this.removeFile = removeFile;
  this.size = size;
  this.upload = upload;
  this.isUploaded = isUploaded;
  this.getUploadPromise = getUploadPromise;
  this.getUploadProgressObservable = getUploadProgressObservable;
  this.bindToScope = bindToScope;
  this.subscribeToSize = subscribeToSize;
  this.size$ = size$;
  this.onUploadComplete = onUploadComplete;

  // private API
  this.$$reloadFile = reloadFile;

  function init() {
    return fileDataCacheService
      .init()
      .then(() => {
        fileCache = fileDataCacheService.getFileCache();
        fileCacheSize = Object.keys(fileCache).length || 0;

        // only happens in cordova
        if (fileCacheSize > 0 && $settings.autoRetryUploads && self.tryUploads) {
          // Wait until we are online, then try to upload
          $ngRedux.state$
            .map(getIsOnline)
            .filter(online => !!online)
            .take(1)
            .subscribe(() => {
              self.tryUploads();
            });
        }
      });
  }

  function onbeforeunload(event) {
    var len = Object.keys(uploadsInProgress).length;
    //Check if there was any change, if no changes, then simply let the user leave
    if (len === 0)
      return;

    var message = 'You are currently uploading ' + len + ' file' + (len === 1 ? '' : 's') + ', are you sure you want to leave?';
    if (typeof event == 'undefined')
      event = window.event;
    if (event)
      event.returnValue = message;
    return message;
  }

  function getFile(key) {
    return files[key];
  }

  function reloadFile(key, file) {
    files[key] = file;
  }

  function getFileData(key) {
    return fileCache[key];
  }

  /**
   * @typedef {Object} PendingUploadsResult
   * @property {Array<Object>} files
   * @property {Object} meta
   * @property {Boolean} meta.hasInProgress
   * @property {Boolean} meta.hasPending
   */

  /**
   * @param id
   * @return {PendingUploadsResult}
   */
  function getByKey(id) {
    const results = [];
    let hasPending = false;
    let hasInProgress = false;

    for (const key in fileCache) {
      if (fileCache.hasOwnProperty(key) && fileCache[key].modelId === id) {
        const inQueue = uploadQueueIndex[key];
        const model = fileCache[key];
        const uploadStatus = model.uploadStatus;

        if (inQueue
          || uploadStatus === UploadStatus.UPLOADING
          || uploadStatus === UploadStatus.UPLOADED
        ) {
          hasInProgress = true;
        } else {
          hasPending = true;
        }

        results.push(model);
      }
    }

    return {
      files: results,
      meta: {
        hasInProgress,
        hasPending,
      },
    };
  }

  /**
   *
   * @param id
   * @return {Observable<PendingUploadsResult>}
   */
  function getByKey$(id) {
    return sizeSubject
      .startWith(fileCacheSize)
      .map(() => getByKey(id));
  }

  function getTotalByKey(id) {
    if (fileCacheSize === 0) {
      return 0;
    } else {
      const total = modelIdTotalCache[id];
      return isNumber(total) ? total : 0;
    }
  }

  function updateObservers(newTotal) {
    if (newTotal === 0) {
      modelIdTotalCache = {};

    } else {
      modelIdTotalCache = {};

      var id;
      var total;
      for (var key in fileCache) {
        if (fileCache.hasOwnProperty(key) && fileCache[key] && fileCache[key].modelId) {
          id = fileCache[key].modelId;
          total = modelIdTotalCache[id];

          if (!total) {
            modelIdTotalCache[id] = 1;
          } else {
            modelIdTotalCache[id] = total + 1;
          }
        }
      }
    }

    sizeSubject.next(newTotal);
  }

  const dataUrlReadCache = {};

  function getReadAsDataURL$(key) {
    if (isAndroidBuild()) {
      // On Android, we can just display the url directly
      const fileData = getFileData(key);
      if (fileData && fileData.fileUri) {
        return Observable.of({
          name: fileData.name,
          type: fileData.type,
          size: fileData.size,
          url: fileData.fileUri,
          progress: null,
        });
      }
    }

    const file = getFile(key);
    const fileUri = getFileUrlData(key);
    if (file && (fileUri || file.fileUri)) {
      return Observable.of({
        name: file.name,
        type: file.type,
        size: file.size,
        url: fileUri || file.fileUri,
        progress: null,
      });

    } else if (dataUrlReadCache[key]) {
      return dataUrlReadCache[key];

    } else if (file) {
      return _readFile(key, file);

    } else if (isIosBuild() && getFileData(key)) {
      // $$retrieveFile is available on iOS. Ignore read errors and just return null.
      return Observable
        .fromPromise(this.$$retrieveFile(key, getFileData(key)).catch(() => null))
        .switchMap(file => {
          if (file) {
            return _readFile(key, file);
          } else {
            return Observable.of(null);
          }
        });

    } else {
      // Should not occur
      return Observable.of(null);
    }
  }

  function _readFile(key, file) {
    const read$ = dataUrlReadCache[key] = readAsDataURL(file);

    read$
      .catch(() => Observable.of(null))
      .subscribe(data => {
        delete dataUrlReadCache[key];
        const file = getFile(key);
        if (file && data && data.url) {
          addFileUrlData(key, {url: data.url});
          file.fileUri = data.url;
        }
      });

    return read$;
  }

  function getAll() {
    const results = [];
    for (const key in fileCache) {
      if (fileCache.hasOwnProperty(key)) {
        results.push(fileCache[key]);
      }
    }
    return results;
  }

  function getAll$() {
    return sizeSubject
      .startWith(fileCacheSize)
      .map(() => getAll());
  }

  function bindToScope(scope, name, id, controllerAs) {
    var vm = isString(controllerAs) ? scope[controllerAs] : scope;

    //console.log('PersistentUploaderService.bindToScope', name, id, controllerAs);

    vm[name] = [];


    var subscription = sizeSubject
      .startWith(fileCacheSize)
      .subscribe(function () {
        scope.$safeApply(function () {
          //console.log('PersistentUploaderService.bindToScope Object.keys(fileCache).length', Object.keys(fileCache).length);
          //console.log('PersistentUploaderService.bindToScope new length', fileCacheSize, id ? Object.keys(self.getByKey(id)).length : Object.keys(self.getAll()).length);

          if (!isUndefined(id)) {
            $filter.$replaceArrayContents(vm[name], self.getByKey(id).files);
          } else {
            $filter.$replaceArrayContents(vm[name], self.getAll());
          }
        });
      });

    scope.$on('$destroy', subscription.unsubscribe.bind(subscription));
  }

  /**
   * Use with angular components
   * @param {$rootScope.Scope} scope
   * @param {function|Rx.Observer} observer
   */
  function subscribeToSize(scope, observer) {
    const subscription = size$()
      .subscribe(observer);

    scope.$on('$destroy', subscription.unsubscribe.bind(subscription));
  }

  function size$() {
    return sizeSubject
      .startWith(fileCacheSize);
  }

  function size() {
    return fileCacheSize;
  }

  /**
   *
   * @param {object} file object with file and uri parameters pointing to the file that is to be uploaded.
   * @param {object} fileData object with id and collection parameters for the firebase object this file is associated with.
   * @returns {string} key cached upload key
   */
  function addFile(file, fileData) {
    //console.log('PersistentUploaderService.addfile', file, fileData);
    fileData = fileData || {};
    const key = fileDataCacheService.generateKey();
    const now = new Date().getTime();

    if (file && fileData && !(key in fileCache)) {
      fileCacheSize++;
    }

    fileCache[key] = {
      key: key,
      modelId: fileData.modelId,
      usage: fileData.usage, /* One of: avatar, gallery, banner, misc */

      onSuccess: fileData.onSuccess,
      onSuccessLocals: fileData.onSuccessLocals,

      name: fileData.name,
      filename: fileData.filename,
      path: fileData.path,
      bucket: fileData.bucket,
      fileUri: fileData.fileUri,
      metadata: fileData.metadata,

      /* HACK: this ust assumes jpeg to get around iOS Safari not attempting to determine the mime type apparently */
      type: file.type, /*  || 'image/jpeg' */
      size: file.size,

      uploadStatus: UploadStatus.PENDING,
      lastUpdated: now,
      dateCreated: now
    };

    files[key] = file;

    //console.log('PersistentUploaderService.addFile cache', angular.copy(fileCache));

    updateObservers(fileCacheSize);

    fileDataCacheService.updateFileCache(fileCache);

    createNotification(fileCache[key]);

    return key;
  }

  /**
   * @param {Array<object>} files object with file and uri parameters pointing to the file that is to be uploaded.
   * @param {Array<object>} attachables object with id and collection parameters for the firebase object this file is associated with.
   * @returns {array} keys cached upload keys
   */
  function addFiles(files, attachables) {
    var result;
    var i;
    var length;

    //console.log('PersistentUploaderService.addFiles', files, attachables);

    // allow the is-array-like FileList
    if ((isArray(files) || files.length) && isArray(attachables) && files.length === attachables.length) {
      result = new Array(files.length);
      for (i = 0, length = files.length; i < length; i++) {
        result[i] = this.addFile(files[i], attachables[i])
      }
    } else if ((isArray(files) || files.length) && isObject(attachables) && files.length) {
      result = new Array(files.length);
      for (i = 0, length = files.length; i < length; i++) {
        result[i] = this.addFile(files[i], attachables)
      }
    } else {
      $log.error('PersistentUploader.addFiles called with invalid arguments');
    }

    return result;
  }

  function addFileUrlData(key, fileUrlData) {
    FileUrlCache.put(key, fileUrlData);
  }

  function getFileUrlData(key) {
    return FileUrlCache.get(key);
  }

  /**
   *
   * @param key
   * @returns {boolean} if the key has already been deleted, the upload was successful, so key not found results in true;
   */
  function isUploaded(key) {
    return !(key in fileCache) || fileCache[key].uploadStatus === UploadStatus.UPLOADED;
  }

  function getUploadPromise(key) {
    return uploadsInProgress[key];
  }

  function getUploadProgressObservable(key) {
    return uploadsInProgress[key] ? uploadsInProgress[key].uploadProgress : null;
  }

  function upload(key) {
    var fileData = fileCache[key];
    if (!fileData) {
      return Promise.reject('PersistentUploaderService: key ' + key + ' not found!');
    } else if (!FirebaseConnectionService.online) {
      return Promise.reject('PersistentUploaderService: offline or low speed connection, cannot upload right now');
    } else if (uploadsCount === MAX_CONCURRENT_UPLOADS - 1) {
      uploadQueue.push(key);
      uploadQueueIndex[key] = true;

      return Promise.resolve(null);

    } else if (key in uploadsInProgress) {
      //console.log('Upload already in progress', fileData, uploadsInProgress[key]);
      return uploadsInProgress[key];
    } else {
      //console.log('creating upload request');
      var request = {
        name: fileData.name,
        filename: fileData.filename,
        path: fileData.path,
        /*bucket: fileData.bucket,*/
        fileUri: fileData.fileUri || null, /* Needed for cordova uploader */
        usage: fileData.usage,
        metadata: fileData.metadata || null /* Provide exif overrides, or similar */
      };

      //console.log('sending upload request to file domain');
      fileData.uploadStatus = UploadStatus.UPLOADING;
      var uploadAttempt = _addToInProgress(key, FileDomain.uploadFile(files[key], request));
      uploadAttempt.uploadComplete.then(noop, handleError, function () {
        $rootScope.$evalAsync(function () {
          fileData.uploadStatus = UploadStatus.UPLOADING;
        });
      });
      uploadAttempt.then(onUploadComplete(key, fileData), handleError);
      return uploadAttempt;
    }

    function handleError(error) {
      _removeFromInProgress(key);

      if (DeviceServices.isWebView()) {
        $rootScope.$evalAsync(function () {
          delete fileData.noteId; // clear any previous note IDs
          fileData.uploadStatus = UploadStatus.PENDING;
          fileData.lastUpdated = new Date().getTime();
          fileDataCacheService.updateFileCache(fileCache);
          //console.log('PersistentUploaderService.upload: failed to upload', fileData);
        });
      } else {
        self.removeFile(key);
      }

      $exceptionHandler(error, 'PersistentUploaderService.upload failed');
      EventBus.broadcast(GlobalEvents.FileUploadError, fileData);
    }
  }

  function createNotification(fileData) {
    const note = {
      name: `Uploading ${fileData.filename}`,
      getNameFromState: (prevNote, nextNote) => {
        switch (nextNote.__noteState) {
          case NoteState.ERROR:
            return `Failed to upload ${fileData.filename}`;

          case NoteState.PROGRESS:
          case NoteState.INDEFINITE:
          case NoteState.INFO:
            return `Uploading ${fileData.filename}`;

          case NoteState.COMPLETE:
            return `Uploaded ${fileData.filename}`;
        }
      },
      removal: NotificationService.REMOVAL.NEVER,
      audio: {
        [NoteState.COMPLETE]: true,
        [NoteState.ERROR]: true,
      },
      toasts: {
        [NoteState.ERROR]: true,
      },
    };

    if (!DeviceServices.isWebView()) {
      const file = files[fileData.key];
      note.actions = {
        [NoteState.ERROR]: {
          className: 'button button-small button-assertive',
          label: 'Retry',
          handler: () => {
            const key = self.addFile(file, fileData);
            self.upload(key);
          },
        },
      };
    } else {
      const key = fileData.key;
      note.toasts[NoteState.COMPLETE] = true;
      note.actions = {
        [NoteState.ERROR]: {
          className: 'button button-small button-assertive',
          label: 'Retry',
          handler: () => self.tryUpload(key), // only available in WebView
        },
      };
    }

    fileData.noteId = NotificationService.addNotification(note);
  }

  function getNotification(fileData) {
    var note = NotificationService.getNotification(fileData.noteId);
    if (!note) {
      createNotification(fileData);
      return NotificationService.getNotification(fileData.noteId);
    } else {
      return note;
    }
  }

  function onUploadComplete(key, fileData) {
    return function onUploadCompleteWeb(fileDomainObject) {
      _removeFromInProgress(key);

      var fileDomainId = fileDomainObject && fileDomainObject.fileId;

      var fileUrlData = FileUrlCache.get(key);
      FileUrlCache.remove(key);
      if (fileDomainId) FileUrlCache.put(fileDomainId, fileUrlData);

      if (!fileData.onSuccess) {
        self.removeFile(key);
      } else {
        var handler = $parse(fileData.onSuccess);
        var locals = extend({},
          isObject(fileData.onSuccessLocals) ? fileData.onSuccessLocals : null,
          {fileDomain: fileDomainObject}
        );

        var result = handler($injector, locals);

        if (result && isFunction(result.then)) {
          result.then(self.removeFile.bind(self, key), function handleError(error) {
            self.removeFile(key);
            $exceptionHandler(error, 'PersistentUploaderService.onUploadComplete failed');
          });
        } else {
          self.removeFile(key);
        }

        return fileDomainObject;
      }
    }
  }

  function removeFile(key) {
    // TODO(design): fire event that key is removed?
    delete files[key];
    delete fileCache[key];

    fileCacheSize = Object.keys(fileCache).length;
    updateObservers(fileCacheSize);

    fileDataCacheService.updateFileCache(fileCache);
  }

  /***********************************************************************************************************************
   * Private Methods
   **********************************************************************************************************************/

  function _addToInProgress(key, uploadPromise) {
    const fileData = fileCache[key];
    if (key in uploadsInProgress) {
      uploadsCount = Object.keys(uploadsInProgress);
    } else {
      uploadsCount++;
    }

    const note = getNotification(fileData);
    NotificationService.updateNotificationPromise(note, uploadPromise);
    uploadPromise.uploadProgress.then(progress$ => NotificationService.updateNotificationProgress(note, progress$));

    uploadsInProgress[key] = uploadPromise;
    return uploadPromise;
  }

  function _removeFromInProgress(key) {
    if (key in uploadsInProgress) {
      delete uploadsInProgress[key];
      uploadsCount--;
    }
    _nextInQueue();
  }

  function _nextInQueue() {
    if (uploadQueue.length > 0) {
      const key = uploadQueue.shift();
      delete uploadQueueIndex[key];
      upload(key);
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/upload-service/PersistentUploaderService.js